import { useQuery } from "@tanstack/react-query";
import {
  Activity,
  BookOpen,
  Building2,
  Command,
  LayoutDashboard,
  Map,
  Search,
  ShieldCheck,
  TicketCheck,
  User,
  X,
} from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { api } from "../api";
import { QUICK_LINKS } from "../navigation";
import type { SearchResult } from "../types";
import { Kbd } from "./UI";

function ResultIcon({ type }: { type: string }) {
  if (type === "user") return <User size={17} />;
  if (type === "organization") return <Building2 size={17} />;
  if (type === "map") return <Map size={17} />;
  if (type === "admin") return <ShieldCheck size={17} />;
  if (type === "invite") return <TicketCheck size={17} />;
  if (type === "business") return <BookOpen size={17} />;
  if (type === "page") return <LayoutDashboard size={17} />;
  return <Search size={17} />;
}

function resultHref(result: SearchResult): string {
  if (result.href) return result.href;
  if (result.type === "user") return `/users/${encodeURIComponent(result.id)}`;
  if (result.type === "organization") return `/organizations/${encodeURIComponent(result.id)}`;
  if (result.type === "map") return `/maps/${encodeURIComponent(result.id)}`;
  return "/";
}

export function CommandPalette({ open, onClose, onNavigate }: { open: boolean; onClose: () => void; onNavigate: (href: string) => void }) {
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [active, setActive] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedQuery(query.trim()), 180);
    return () => window.clearTimeout(timer);
  }, [query]);
  useEffect(() => {
    if (open) {
      window.setTimeout(() => inputRef.current?.focus(), 20);
    }
  }, [open]);

  const search = useQuery({
    queryKey: ["search", debouncedQuery],
    queryFn: async () => {
      const payload = await api<{ groups?: Array<{ type: string; label: string; items: Array<Omit<SearchResult, "type"> & { type?: string }> }> }>(`/api/search?q=${encodeURIComponent(debouncedQuery)}`);
      const singular: Record<string, string> = { users: "user", organizations: "organization", maps: "map", invites: "invite", admins: "admin", cleanup: "cleanup" };
      return (payload.groups ?? []).flatMap((group) => group.items.map((item) => ({ ...item, type: item.type || singular[group.type] || group.type })));
    },
    enabled: open && debouncedQuery.length > 1,
    staleTime: 15_000,
  });

  const local = useMemo(() => {
    const needle = query.toLowerCase().trim();
    if (!needle) return QUICK_LINKS;
    return QUICK_LINKS.filter((item) => `${item.title} ${item.subtitle} ${item.keywords}`.toLowerCase().includes(needle));
  }, [query]);
  const results = debouncedQuery.length > 1 ? [...local, ...(search.data ?? [])] : local;
  const close = () => {
    setQuery("");
    setDebouncedQuery("");
    setActive(0);
    onClose();
  };
  const choose = (result: SearchResult) => {
    onNavigate(resultHref(result));
    close();
  };

  if (!open) return null;
  return (
    <div className="command-backdrop" onMouseDown={(event) => { if (event.target === event.currentTarget) close(); }}>
      <div className="command-palette" role="dialog" aria-modal="true" aria-label="Search Suppose admin">
        <div className="command-input">
          <Search size={19} />
          <input
            ref={inputRef}
            value={query}
            onChange={(event) => { setQuery(event.target.value); setActive(0); }}
            onKeyDown={(event) => {
              if (event.key === "Escape") close();
              if (event.key === "ArrowDown") { event.preventDefault(); setActive((value) => Math.min(results.length - 1, value + 1)); }
              if (event.key === "ArrowUp") { event.preventDefault(); setActive((value) => Math.max(0, value - 1)); }
              if (event.key === "Enter" && results[active]) choose(results[active]);
            }}
            placeholder="Find a user, invite, team, Board, or page"
            aria-label="Global search"
          />
          {search.isFetching ? <span className="command-loading" /> : query ? <button type="button" aria-label="Clear search" onClick={() => setQuery("")}><X size={16} /></button> : <Kbd>esc</Kbd>}
        </div>
        <div className="command-results">
          <div className="command-section-label">{debouncedQuery.length > 1 ? "Matches" : "Go somewhere"}</div>
          {results.length ? results.map((result, index) => (
            <button type="button" className={`command-result${active === index ? " is-active" : ""}`} key={`${result.type}:${result.id}:${index}`} onMouseMove={() => setActive(index)} onClick={() => choose(result)}>
              <span className={`result-icon result-${result.type}`}><ResultIcon type={result.type} /></span>
              <span><strong>{result.title}</strong><small>{result.subtitle || result.type}</small></span>
              <span className="result-type">{result.type === "map" ? "Board" : result.type}</span>
            </button>
          )) : (
            <div className="command-empty"><Command size={22} /><strong>No matching records yet</strong><span>Try a name, email, invite ID, or Board ID.</span></div>
          )}
        </div>
        <div className="command-footer"><span><Kbd>↑</Kbd><Kbd>↓</Kbd> move</span><span><Kbd>↵</Kbd> open</span><span><Activity size={13} /> Live catalog search</span></div>
      </div>
    </div>
  );
}
